import Extent from '@arcgis/core/geometry/Extent';
import { geodesicBuffer } from '@arcgis/core/geometry/geometryEngine';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import { DEFAULT_MAP_SOURCE } from '@env';

// Removed EsriMap import to avoid circular dependency
import { GoogleMap } from '@lib/geo/google/map';
import { FsMap } from '@lib/geo/map';

import { FsMapShape } from '../shapes';

const defaultMapSource: string = DEFAULT_MAP_SOURCE;

export function calculateEsriBounds(geometry: any): Extent {
  let xmin = Infinity;
  let ymin = Infinity;
  let xmax = -Infinity;
  let ymax = -Infinity;

  if (geometry.type === 'polygon') {
    geometry.rings?.forEach((ring) => {
      ring.forEach((point) => {
        const [x, y] = point;
        if (x < xmin) xmin = x;
        if (y < ymin) ymin = y;
        if (x > xmax) xmax = x;
        if (y > ymax) ymax = y;
      });
    });
  } else if (geometry.type === 'polyline') {
    geometry.paths?.forEach((path) => {
      path.forEach((point) => {
        const [x, y] = point;
        if (x < xmin) xmin = x;
        if (y < ymin) ymin = y;
        if (x > xmax) xmax = x;
        if (y > ymax) ymax = y;
      });
    });
  } else if (geometry.type === 'point') {
    xmin = xmax = geometry.x;
    ymin = ymax = geometry.y;
  }

  return new Extent({
    xmin,
    ymin,
    xmax,
    ymax,
    spatialReference: geometry.spatialReference,
  });
}

export function processPoints(geometry: any, callback: any, thisArg: any): any {
  if (defaultMapSource === 'google') {
    if (geometry instanceof google.maps.LatLng) {
      callback.call(thisArg, geometry);
    } else if (geometry instanceof google.maps.Data.Point) {
      callback.call(thisArg, geometry.get());
    } else {
      geometry.getArray().forEach((g) => {
        processPoints(g, callback, thisArg);
      });
    }
  } else if (defaultMapSource === 'esri') {
    if (Array.isArray(geometry)) {
      geometry.forEach((point) => {
        callback.call(thisArg, point);
      });
    } else if (geometry instanceof Point) {
      callback.call(thisArg, [geometry.longitude, geometry.latitude]);
    } else {
      geometry.forEach((ring) => {
        processPoints(ring, callback, thisArg);
      });
    }
  }
}

export function focusShape(fsShape: any, map: FsMap): void {
  let bounds;
  const shape = fsShape.shape || new FsMapShape(fsShape).shape;

  if (defaultMapSource === 'google') {
    bounds = new google.maps.LatLngBounds();
    switch (true) {
      case shape instanceof google.maps.Polygon: {
        const points = shape.getPath();
        processPoints(points, bounds.extend, bounds);
        break;
      }
      case shape instanceof google.maps.Circle: {
        const circlePoints = circlePath(shape.getCenter(), shape.getRadius(), 360).map(
          (coor) => new google.maps.LatLng(coor[0], coor[1]),
        );
        const geometry = new google.maps.Data.Polygon([circlePoints]);
        processPoints(geometry, bounds.extend, bounds);
        break;
      }
    }
    (map as unknown as GoogleMap).mapInstance.fitBounds(bounds);
  } else if (defaultMapSource === 'esri') {
    const extent = calculateEsriBounds(shape.geometry);
    (map as any).mapView.goTo(extent);
  }
}

function circlePath(
  center: { lat: number; lng: number },
  radius: number,
  points: number,
): google.maps.LatLng[] | number[][] {
  const paths = [];
  const factor = 360 / points;
  let distance = 0;

  if (defaultMapSource === 'google') {
    for (let i = 0; i < points; i += 1, distance += factor) {
      const lat = google.maps.geometry.spherical.computeOffset(center, radius, distance).lat();
      const lng = google.maps.geometry.spherical.computeOffset(center, radius, distance).lng();
      paths.push([lat, lng]);
    }
  } else if (defaultMapSource === 'esri') {
    for (let i = 0; i < points; i += 1, distance += factor) {
      const point = new Point({
        latitude: center.lat,
        longitude: center.lng,
        spatialReference: { wkid: 4326 },
      });

      const bufferResult = geodesicBuffer(point, radius, 'meters', false);
      let bufferPolygon: Polygon;

      if (Array.isArray(bufferResult)) {
        bufferPolygon = bufferResult[0];
      } else {
        bufferPolygon = bufferResult as Polygon;
      }

      const ring = bufferPolygon.rings[0];
      const step = Math.floor(ring.length / points);
      for (let i = 0; i < ring.length; i += step) {
        paths.push([ring[i][1], ring[i][0]]);
      }
    }
  }

  return paths;
}
